<template>
  <section class="orderItems_table_wrap">
      <!-- scroll-width="120rem" -->
    <ve-table
      :fixed-header="true"
      max-height="100%"
      :columns="columns"
      :table-data="tableData"
      :border-around="false"
      :sort-option="sortOption"
    />

    <div>
      <div v-if="isLoading" class="loader_and_empty">
        <spinner />
        <span>{{ 'Order is loading' }}</span>
      </div>
      <empty-state
        v-else-if="!isLoading && !order_items.length"
        title="Empty order items"
      />
    </div>
    <DeliveryModal
      v-if="showDeliveryModal"
      :show="showDeliveryModal"
      :order="order"
      :merchant="merchant"
      @onClose="onDeliveryClose"
      @onSuccess="onDeliverySuccess"
    />
  </section>
</template>

<script>
import { VeTable } from 'vue-easytable';
import alertMixin from 'shared/mixins/alertMixin';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import  CouponsApi from 'dashboard/api/coupons.js';
import responsiveMixin from 'shared/mixins/responsiveMixin.js';
import DeliveryModal from "./DeliveryModal.vue"

export default {
    components:{
        VeTable,
        Spinner,
        EmptyState,
        DeliveryModal,
    },
    mixins: [alertMixin,responsiveMixin,],
    props:{
        order_items:{
            type: Array,
            default: () => []
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isOrderCancelOrComplete: {
            type: Boolean,
            default: false,
        },
        cols: {
            type: Array,
            default: () => [],
        },
        merchant: {
          type: Object,
          default: {},
        },
        order: {
          type: Object,
          default: {},
        },
        paymentNotify: {
          type: Object,
          default: {},
        },
        isRefundBankFetching: {
          type: Boolean,
          default: false,
        },
        onRefundAmount: {
          type: Function,
          default: function(){},
        },
        catalogItems: {
          type: Array,
          default: ()=>([]),
        },

    },
    data(){
        return {
          sortConfig: {},
          sortOption: {
            sortAlways: true,
            sortChange: params => this.$emit('on-sort-change', params),
          },
          promo_code_loading: false,
          showDeliveryModal: false,
        }
    },
    computed:{
        tableData(){
            // console.log(this.cols,this.order_items);
            if (this.isLoading || !this.order_items?.length) {
                return [];
            }

            // pending, fresh and paid
            // delivery can only change

            let hasItem = false;
            const formattedOrders = this.order_items.map(item=>{
              if (item.id && (item.quantity > 0)) {
                hasItem = true;
              }
                const preSet = {
                  ...item,
                  total:parseFloat(parseFloat(item.item_price) * (parseInt(item.quantity)||0).toFixed(2))
                }
                if (!('original_inventory' in item)) {
                  preSet.original_inventory = item.available_inventory + item.quantity;
                }
                return preSet;
            })

            // calculate total, sst, grantTotal here
            const sumTotal = formattedOrders.reduce((acc, item) => acc + parseFloat(item.item_price) * (parseInt(item.quantity)||0), 0);

            const sst = this.merchant.sst === 'true' ? parseFloat(sumTotal * 0.08) : 0;
            // const sst = parseFloat(sumTotal * 0.08);
            const delivery_price = this.order.deliveryModeItem?.amount || this.order.shipping_total ||0;
            let grantTotal = sumTotal + sst + delivery_price;
            if (this.order.discount_total) {
              grantTotal -= this.order.discount_total;
            }

            const sumRows = [
              {
                item_price: "Total",                        // at 'item_price' column display text 'Total'
                total: parseFloat(sumTotal.toFixed(2)),      // at 'total' column display sum amount
                isTitle: true,
                class:"font_500"
              },
              {
                item_price: "Courier Fee",
                total: parseFloat((this.order.shipping_total ||0).toFixed(2)),
                isTitle: true,
                class:"font_500"
              },
              {
                item_price: "SST",                          // at 'item_price' column display text 'SST'
                total: parseFloat(sst.toFixed(2)),           // at 'total' column display sst amount
                isTitle: true,
                class:"font_500"
              },
              {
                item_price: "Discount",                          // at 'item_price' column display text 'SST'
                total: "- " + (this.order.discount_total ?? 0).toFixed(2),           // at 'total' column display sst amount
                isTitle: true,
                isCoupon: true,
                class:"font_500",
              },
              {
                item_price: "Delivery",
                total: delivery_price,
                isTitle: true,
                isDelivery: true,
                hasItem,
                class:"font_500",
              },
              // add check coupon
              {
                item_price: "Grand Total",                        // at 'item_price' column display text 'Grand Total'
                total: parseFloat(grantTotal.toFixed(2)),          // at 'total' column display grantTotal amount
                isTitle: true,
                class:"font_600"
              },
              {
                item_price: "Paid Amount",
                // total: parseFloat((this.order.paid_total || 0).toFixed(2)),
                total: this.toTwoDecimal(this.order.paid_total),
                isTitle: true,
                isPayMessage: true,
                class:"font_500"
              },
              {
                item_price: "Refund Amount",
                total: parseFloat((this.order.refund_amount || 0).toFixed(2)), // add inupt field so user can update
                isTitle: true,
                isRefundAmount: true,
                class:"font_500"
              },

            ]
            return [
              ...formattedOrders,
              ...sumRows,
            ]
        },
        columns(){
            let matches = {};

            matches.item_name = {
                field: 'item_name',
                key: 'item_name',
                title: 'Item Name',
                fixed: 'left',
                align: 'left',
                width: 240,
                sortBy: this.sortConfig.item_name || '',
                renderBodyCell: ({row,rowIndex })=>{
                  const onSelectItem = (item,row) =>{
                    this.$emit('onSelectProductItem',{item,index:rowIndex})
                  }
                  if (row.isTitle) {
                    return <span></span>
                  }
                    return (
                      <multiselect
                        value={this.catalogItems.find(opt => opt.id === row.catalog_item_id)}
                        options={this.catalogItems}
                        track-by="id"
                        label="item_name"
                        multiple={false}
                        close-on-select={true}
                        clear-on-select={false}
                        hide-selected={true}
                        tag-placeholder="''"
                        taggable={false}
                        placeholder={'select item'}
                        selected-label={''}
                        select-label={''}
                        deselect-label={''}
                        maxHeight={200}
                       onSelect={(option) => onSelectItem(option, row)}
                    />
                    )
                }
            }
            matches.item_price = {
                field: 'item_price',
                key: 'item_price',
                title: `Price`,
                currency: this.order?.currency,
                align: 'right',
                width: 240,
                sortBy: this.sortConfig.item_price || '',
                renderBodyCell: ({row,rowIndex})=>{
                    const price = typeof row.item_price === "number"
                    ? row.item_price.toFixed(2) : row.item_price;
                    if (row.isTitle) {
                      return <span class={["order_edit_tbl_title",row.class].join(" ")}>{price}</span>
                    }

                    // return <span class={row.isTitle ? "order_edit_tbl_title":""}>{price}</span>
                    return <div>
                      <input
                        class={this.isOrderCancelOrComplete ? "disable_field remove_field_spin" :"remove_field_spin"}

                        style="text-align: right; padding-right:10px; font-size:14px;"
                        type="number"
                        value={row.item_price}
                        min="0"
                        onKeydown={(e) => this.handlePrice(e, row,rowIndex)}
                        onKeyup={(e) => this.handlePrice(e, row,rowIndex)}
                        onChange={(e) => this.handlePrice(e, row,rowIndex)}
                      />
                    </div>
                },
                renderHeaderCell:({ column })=>{
                    return <span class="order_head_col">
                        <span>{column.title}</span>
                        <span class="order_head">({column.currency})</span>
                    </span>
                }
            }
            matches.quantity = {
                field: 'quantity',
                key: 'quantity',
                title: 'Quantity',
                align: 'right',
                width: 240,
                sortBy: this.sortConfig.quantity || '',
                renderBodyCell: ({row, rowIndex })=>{
                  // console.log({rowIndex });
                    // return <span>{row.quantity}</span>
                    if (typeof row.quantity !== "number") {
                      return <span></span>
                    }
                    return <div>
                      <input
                        class={this.isOrderCancelOrComplete ? "disable_field remove_field_spin" :"remove_field_spin"}

                        style="text-align: right; padding-right:10px; font-size:14px;"
                        type="number"
                        value={row.quantity}
                        disabled={this.isOrderCancelOrComplete}
                        step="1"
                        min="0"
                        onKeydown={(e) => this.handleQuantity(e, row,rowIndex)}
                        onKeyup={(e) => this.handleQuantity(e, row,rowIndex)}
                        onChange={(e) => this.handleQuantity(e, row,rowIndex)}
                      />
                    </div>
                }
            }
            matches.total = {
                field: 'total',
                key: 'total',
                title: `Total`,
                currency: this.order?.currency,
                align: 'right',
                width: 240,
                sortBy: this.sortConfig.total || '',
                renderBodyCell: ({row})=>{
                  const price = typeof row.total === "number"
                    ? row.total.toFixed(2) : row.total;
                    return <span class={row.isTitle ? "order_edit_tbl_title " + row.class:""}>
                      {price}
                    </span>
                },
                renderHeaderCell:({ column })=>{
                  return <span class="order_head_col">
                    <span>{column.title}</span>
                    <span class="order_head">({column.currency})</span>
                  </span>
                }
            }
            matches.available_inventory = {
                field: 'available_inventory',
                key: 'available_inventory',
                title: 'Inventory',
                align: 'right',
                width: 240,
                sortBy: this.sortConfig.available_inventory || '',
                renderBodyCell: ({row})=>{
                  if (row.isTitle && row.isPayMessage) {
                    return <span class={row.isTitle ? "order_edit_tbl_title " + row.class:""}>
                      <span class={`pay_msg ${this.paymentNotify.msgClass}`}>{this.paymentNotify.message}</span>
                    </span>
                  }

                  if (row.isCoupon) {
                    if (!this.order.promo_code) {
                      return <div>
                      <input
                        id="promo_code_input_ID"
                        class="order_item_input_field text_14"
                        style="padding-right:10px;"
                        placeholder="Write your coupon"
                        value={this.order.promo_code_input}
                        onChange={(e) => this.onPutNewPromoCode(e)}
                      />
                    </div>
                    }
                    return <div class="font_regular font_500 text_14 text_success">{this.order.promo_code}</div>
                  }
                  if (row.isDelivery) {
                    return this.order.deliveryModeItem?.title;
                  }

                  if(row.isRefundAmount && (this.order.paid_total > 0)){
                    // allow refund always when paid > 0
                    return <woot-button
                        variant="smooth"
                        size="small"
                        color-scheme="primary"
                        class="button small refund_btn"
                        disabled={this.isRefundBankFetching}
                        is-loading={this.isRefundBankFetching}
                        onClick={this.onRefundAmount}
                      > Refund
                      </woot-button>
                  }

                  return <span>{row.available_inventory}</span>
                }
            }
            matches.action = {
                field: 'action',
                key: 'action',
                title: 'Action',
                align: 'center',
                width: 240,
                renderBodyCell: ({row})=>{
                  if (row.isCoupon) {
                    if (!this.order.promo_code) {

                      return <div>
                      <woot-button
                        variant="smooth"
                        size="small"
                        color-scheme="secondary"
                        class="refund_btn"
                        isLoading={this.promo_code_loading}
                        disabled={this.promo_code_loading}
                        onClick={()=>this.checkPromoCode()}
                         onKeydown={(e) => this.clearPromoCode()}
                      >
                      {this.$t('ORDER.DETAILS.INFO.COUPON.CHECK_BTN')}
                      </woot-button>
                    </div>
                    }
                  }

                  const deliverableChangeAbleStatus = ['fresh','pending','paid'];
                  if (row.isDelivery && row.hasItem && deliverableChangeAbleStatus.includes(this.order.order_status?.id)) {
                    return <woot-button
                      variant="smooth"
                      size="small"
                      color-scheme="primary"
                      class="button small refund_btn"
                      onClick={this.onDeliveryOpen}
                      > Set Delivery Information
                      </woot-button>
                  }

                  if (typeof row.id != "number") {
                    return <span></span>
                  }

                  const enableStatuslList = ['fresh','pending','packed','paid'];
                  const isDisable = !enableStatuslList.includes(this.order?.order_status?.id);

                    return (
                    <div>
                      <woot-button
                        variant="smooth"
                        size="small"
                        color-scheme="alert"
                        class="order_item_del"
                        icon="delete"
                        disabled={isDisable}
                        onClick={()=>this.deleteItem(row)}
                      >
                      </woot-button>
                    </div>
                    )
                }
            }







            const preSet = [];
            this.cols.forEach(col=>{
                if (col.visible) {
                    if (col.key in matches) {
                        preSet.push(matches[col.key])
                    }
                }
            })
            return preSet;
        },
        // end computed
    },

    methods:{
      handleQuantity(e,rowData,rowIndex){
        // console.log({rowData});
        const newQuantity  = parseInt(e.target.value||"0");
        const oldQuantity = rowData.quantity;
        const isIncrease = newQuantity > oldQuantity;
        if (isIncrease) {
            // check not exeeding available
            if (rowData.available_inventory <= 0) {
              this.showAlert(`You can add a maximum of ${rowData.available_inventory} quantities of ${rowData.item_name}`);
              // Revert to previous quantity value
              e.target.value = rowData.quantity;
              return;
            }else if(newQuantity > rowData.original_inventory){
              e.target.value = rowData.quantity;
              this.showAlert(`Insufficient inventory!`);
            }else{
              this.$emit('onQuantityChange',e,rowData,rowIndex)
            }
        }else{
          // chek not less than 0
          if (newQuantity < 0) {
            this.showAlert(`Invalid quantity!`);
          }else{
            this.$emit('onQuantityChange',e,rowData,rowIndex)
          }
        }
      },
      handlePrice(e,rowData,rowIndex){
        // console.log({rowData});
        const newPrice = parseFloat(e.target.value||"0");
        if (newPrice && newPrice < 0) {
          this.showAlert('Invalid price!');
          e.target.value = rowData.item_price; // revert to old value
          return;
        }
        this.$emit('onPriceChange', e,rowData,rowIndex);

      },
      deleteItem(row){
        const index = this.order_items.findIndex(item => item.id === row.id);
        if (index > -1) {
            this.order_items.splice(index, 1);
        }
      },
      onPutNewPromoCode(e){
        this.order.promo_code_input = e.target.value;
      },
      clearPromoCode(){
        this.order.promo_code = "";
      },
      async checkPromoCode(){
        try {
          this.promo_code_loading = true;
          const {data} = await CouponsApi.applyCoupon({
            code: this.order.promo_code_input,
            phone_number: this.order?.contact?.phone_number,
            items: this.order.order_items.map((item)=>{
              return {
                item_price: item.item_price,
                quantity:item.quantity,
                catalog_item_id: item.catalog_item_id,
              }
            })
          })

          this.order.new_promo_code = data.code;
          this.order.discount_total = data.discount;
          this.promo_code_loading = false;
        } catch (error) {
          this.promo_code_loading = false;
          // promo_code
            console.log(error,error.message,error.response);
          this.showAlert(error?.response?.data?.error || this.$t('ORDER.DETAILS.INFO.COUPON.ERROR_MSG'));
          await this.$nextTick();
          this.order.promo_code_input = '';
          this.order.new_promo_code = '';
          document.getElementById("promo_code_input_ID").value = "";
          await this.$nextTick();
          console.log(this.order);
        }
      },
      onDeliveryOpen(){
        this.showDeliveryModal = true;
      },
      onDeliveryClose(){
        this.showDeliveryModal = false;
      },
      onDeliverySuccess(){
        this.onDeliveryClose()
      },
      toTwoDecimal(value){
        const num = Number(value)
        return isNaN(num) ? 0 : Number(num.toFixed(2))
      },
      // end methods
    },

}
</script>




<style lang="scss" scoped>
@import '~dashboard/assets/scss/mixins';
@import '~dashboard/assets/scss/widgets/_fonts.scss';

.orderItems_table_wrap {
  flex: 1 1;
  height: 100%;
  overflow: hidden;
}

.orderItems_table_wrap::v-deep {
  .ve-table {
    padding-bottom: var(--space-large);
    .ve-table-container{
      overflow-y: hidden;
      .ve-table-content{
        border-radius: 8px;
        overflow: hidden;
      }
    }
  }

  .ve-table-header-th {
    padding: var(--space-small) var(--space-two) !important;
    background-color: var(--w-500) !important;
    color: var(--white) !important;
    text-transform: capitalize;
  }

  .ve-table-body-td {
    padding: var(--space-small) var(--space-two) !important;
  }

  .ve-table-header-th {
    font-size: 14px;
  }
  .ve-table-sort {
    top: -4px;
  }

}

.loader_and_empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-default);
  padding: var(--space-big);
}

.remove_field_spin {
    font-weight: 500;
    // background: #EEEEEE;
    /* Hide arrows for Chrome, Safari, and Edge */
    -webkit-appearance: none; /* Remove default appearance */
    -moz-appearance: textfield; /* Remove default appearance in Firefox */
    appearance: none; /* Remove default appearance in modern browsers */
}

/* Specific to WebKit browsers */
.remove_field_spin::-webkit-inner-spin-button,
.remove_field_spin::-webkit-outer-spin-button {
    display: none; /* Hide the spin buttons */
}

.order_edit_tbl_title{
  font-family: Figtree, Inter, sans-serif;
  font-size: 14px;
  color: #1F1F1F;
}

.pay_msg{
  // font-family: Figtree, Inter, sans-serif;
  font-weight: 500;
  // font-size: 12px;
  border-radius: 4px;
  padding: 4px 8px;
  // color: #3c4858;
  margin-left: 10px;
}
.pay_waiting_msg{
  background-color: #F7FEE7;
}
.pay_receive_msg{
  background-color: #F2E7FE;
}
.pay_refund_msg{
  background-color: #E5F1FF;
}
.refund_btn{
  padding: var(--space-smaller) 1em;;
}
.disable_field{
    background-color: #ededed;
}
.order_item_input_field{
    // background: no-repeat;
    border: 1px solid #eee;
    padding: 6px;
}
</style>

<style>
.orderItems_table_wrap .ve-table-body-td{
  font-size: 14px;
  font-weight: 500;
  color: #1F1F1F !important;
}
.orderItems_table_wrap .order_head_col{
    /* width: 80%; */
    display: inline-grid;
    text-align: right;
}
.orderItems_table_wrap .order_head_col .order_head{
    display: inline-block;
    width: 100%;
    font-size: 10px;
}
.orderItems_table_wrap .order_item_del,
.orderItems_table_wrap .order_item_del:hover{
  background: #fff !important;
}
.orderItems_table_wrap .order_item_del svg{
  width: 20px;
  height: 20px;
  color: #E0E0E0;
}
.orderItems_table_wrap  .multiselect__content-wrapper {
  position: relative;
}
.orderItems_table_wrap  .multiselect__content-wrapper .multiselect__element {
  padding-left: 4px;
}
.orderItems_table_wrap  .multiselect__placeholder {
  padding-left: 4px;
}

</style>